import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, MapPin, ArrowRight, Sparkles, CheckCircle2, ShieldCheck, Users, Star } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { POPULAR_SERVICES, HERO_IMAGE, SECONDARY_IMAGE, TOP_CITIES } from "@/lib/constants";
import ProviderCard from "@/components/ProviderCard";
import ProvidersMap from "@/components/ProvidersMap";
import TreatmentRecommender from "@/components/TreatmentRecommender";
import AISearchBar from "@/components/AISearchBar";
import SEO from "@/components/SEO";

export default function Home() {
  const nav = useNavigate();
  const [featured, setFeatured] = useState([]);
  const [topCities, setTopCities] = useState([]);
  const [stats, setStats] = useState({ providers: 0, cities: 0, states: 0, reviews: 0 });

  useEffect(() => {
    api.get("/providers/featured?limit=6").then(({ data }) => setFeatured(data));
    api.get("/cities/top").then(({ data }) => setTopCities(data));
    api.get("/stats").then(({ data }) => setStats(data));
  }, []);

  return (
    <>
      <SEO
        title="IV Therapy Finder — Find Trusted IV Therapy & Mobile Infusion Providers Near You"
        description={`Search ${stats.providers || 'thousands of'} verified IV therapy clinics, mobile providers, NAD+ specialists, and hydration experts across the United States.`}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "IV Therapy Finder",
          url: typeof window !== "undefined" ? window.location.origin : "https://ivtherapyfinder.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "{search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#F3F4F1] to-[#FAFAFA]">
        <div className="absolute inset-0">
          <img src={HERO_IMAGE} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAFAFA] via-[#FAFAFA]/70 to-transparent" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-20 md:pt-24 md:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white border border-stone-200 mb-6 text-xs font-medium text-stone-700 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
              The #1 IV Therapy Directory in the U.S.
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900 leading-[1.05] mb-5">
              Find Trusted IV Therapy & <span className="text-[#064E3B]">Mobile Infusion</span> Providers Near You
            </h1>
            <p className="text-base sm:text-lg text-stone-600 leading-relaxed mb-8 max-w-2xl">
              Search thousands of IV therapy clinics, mobile providers, NAD+ specialists, and hydration experts across the United States — verified, reviewed, and ready to book.
            </p>

            {/* AI-powered Search */}
            <AISearchBar />

            <div className="mt-5 flex items-center gap-5 text-xs text-stone-500">
              <Link to="/add-business" className="font-medium hover:text-[#064E3B] underline-offset-4 hover:underline" data-testid="hero-list-business">
                List your business free →
              </Link>
              <TreatmentRecommender
                trigger={
                  <button className="font-medium text-[#064E3B] hover:underline underline-offset-4 inline-flex items-center gap-1" data-testid="hero-recommender-cta">
                    <Sparkles className="w-3 h-3" /> Not sure what you need? Take our AI quiz
                  </button>
                }
              />
            </div>
          </motion.div>

          {/* trust stats */}
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
            {[
              { icon: Users, k: stats.providers || "10,000+", l: "Verified Providers" },
              { icon: MapPin, k: stats.cities || "500+", l: "Cities Covered" },
              { icon: Star, k: stats.reviews || "25,000+", l: "Patient Reviews" },
              { icon: ShieldCheck, k: "100%", l: "Free to Search" },
            ].map((t, i) => (
              <div key={i} className="bg-white/80 backdrop-blur rounded-xl p-4 border border-stone-200/70">
                <t.icon className="w-4 h-4 text-[#064E3B] mb-2" />
                <div className="font-display text-xl font-semibold text-stone-900">{t.k}</div>
                <div className="text-xs text-stone-500">{t.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* POPULAR SERVICES */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-2">Popular Treatments</div>
            <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
              Browse by service
            </h2>
          </div>
          <p className="text-sm text-stone-500 max-w-md">
            From hangover recovery to NAD+ longevity protocols — explore the most-requested IV therapy treatments.
          </p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {POPULAR_SERVICES.map((s, i) => (
            <Link
              key={s.slug}
              to={`/search?service=${encodeURIComponent(s.name)}`}
              className="group relative bg-white rounded-2xl p-5 border border-stone-200/70 hover:border-[#064E3B]/40 hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
              data-testid={`service-card-${s.slug}`}
            >
              <div className="w-11 h-11 rounded-xl bg-[#064E3B]/5 group-hover:bg-[#064E3B] flex items-center justify-center mb-3 transition">
                <s.icon className="w-5 h-5 text-[#064E3B] group-hover:text-white transition" strokeWidth={1.8} />
              </div>
              <div className="font-display text-sm font-semibold text-stone-900 leading-tight">{s.name}</div>
              <ArrowRight className="w-4 h-4 text-stone-300 group-hover:text-[#064E3B] absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition" />
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PROVIDERS */}
      <section className="py-20 bg-white border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-end justify-between mb-10">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-2">Featured providers</div>
              <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
                Top-rated IV therapy near you
              </h2>
            </div>
            <Link to="/search" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-[#064E3B] hover:gap-2 transition-all" data-testid="featured-view-all">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((p) => <ProviderCard key={p.id} p={p} />)}
          </div>
        </div>
      </section>

      {/* TOP CITIES + MAP */}
      <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-2">Service Area</div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900 mb-3">
            Top cities served
          </h2>
          <p className="text-stone-500 max-w-xl mx-auto">
            From coast to coast — explore verified IV therapy providers in the most popular metros.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <ProvidersMap
              markers={(topCities.length ? topCities : TOP_CITIES).map((c, i) => ({
                id: i, city: c.city, state: c.state, lat: c.lat || 39.5, lng: c.lng || -98.35, count: c.count || Math.floor(Math.random() * 12) + 3
              }))}
              height="540px"
              kind="city"
              fitBounds={true}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 content-start">
            {(topCities.length ? topCities : TOP_CITIES).slice(0, 10).map((c, i) => (
              <Link
                key={i}
                to={`/search?city=${encodeURIComponent(c.city)}`}
                className="group bg-white rounded-xl p-4 border border-stone-200/70 hover:border-[#064E3B]/40 hover:shadow-md transition-all"
                data-testid={`top-city-${c.city.toLowerCase().replace(/\s/g, '-')}`}
              >
                <div className="font-display text-sm font-semibold text-stone-900 group-hover:text-[#064E3B] transition">{c.city}</div>
                <div className="text-[11px] text-stone-500 mt-0.5">{c.state}</div>
                {c.count && <div className="text-[10px] uppercase tracking-wider text-[#10B981] mt-1.5 font-semibold">{c.count} providers</div>}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-[#F3F4F1] relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-3">For Patients</div>
              <h3 className="font-display text-2xl sm:text-3xl font-semibold text-stone-900 mb-6">Find your perfect provider in 4 steps</h3>
              <ol className="space-y-5">
                {[
                  ["Search", "Browse providers by city, service, or rating"],
                  ["Compare", "Side-by-side pricing, reviews, and services"],
                  ["Read Reviews", "Real patient stories with AI-generated summaries"],
                  ["Contact or Book", "Direct phone, text, or appointment request"],
                ].map(([t, d], i) => (
                  <li key={i} className="flex gap-4">
                    <div className="w-9 h-9 rounded-full bg-[#064E3B] text-white flex items-center justify-center font-display font-semibold text-sm shrink-0">{i + 1}</div>
                    <div>
                      <div className="font-display text-base font-semibold text-stone-900">{t}</div>
                      <div className="text-sm text-stone-600">{d}</div>
                    </div>
                  </li>
                ))}
              </ol>
              <TreatmentRecommender />
            </div>

            <div className="bg-[#064E3B] rounded-3xl p-8 lg:p-10 text-white relative overflow-hidden">
              <img src={SECONDARY_IMAGE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#B4A081] mb-3">For Businesses</div>
                <h3 className="font-display text-2xl sm:text-3xl font-semibold mb-6">Grow your IV therapy business</h3>
                <ul className="space-y-3.5 mb-8">
                  {["Get listed in front of patients actively searching", "Receive qualified appointment leads daily", "Manage reviews and reputation", "Track analytics and conversion in real-time"].map((t) => (
                    <li key={t} className="flex gap-3 items-start text-sm text-white/90">
                      <Sparkles className="w-4 h-4 text-[#B4A081] shrink-0 mt-0.5" /> {t}
                    </li>
                  ))}
                </ul>
                <Button asChild className="bg-white text-[#064E3B] hover:bg-stone-100 rounded-full font-semibold" data-testid="how-it-works-cta">
                  <Link to="/add-business">List your business — free <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATES DIRECTORY */}
      <StatesDirectory />
    </>
  );
}

function StatesDirectory() {
  const [states, setStates] = useState([]);
  useEffect(() => {
    api.get("/seo/states").then(({ data }) => setStates(data));
  }, []);
  if (!states.length) return null;
  return (
    <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8 text-center">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#10B981] mb-2">Browse by state</div>
        <h2 className="font-display text-3xl font-semibold text-stone-900">IV therapy across the U.S.</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {states.map((s) => (
          <Link
            key={s._id}
            to={`/iv-therapy/${s._id.toLowerCase().replace(/\s+/g, "-")}`}
            className="px-3 py-2.5 rounded-lg bg-white border border-stone-200 hover:border-[#064E3B]/40 hover:shadow-sm text-sm font-medium text-stone-800 transition"
            data-testid={`state-link-${s._id.toLowerCase().replace(/\s+/g, "-")}`}
          >
            {s._id} <span className="text-xs text-stone-400">· {s.count}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
